import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Crown, Loader2, Check, X } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { ShaderBackground } from "@/components/ui/hero";
import { createCheckoutSession } from "@/lib/subscriptions.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { planPriceIdFor } from "@/lib/pricing";
import { useLocale } from "@/lib/i18n";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useIsMobile } from "@/hooks/use-mobile";
import stripeLogoAsset from "@/assets/stripe-logo.svg.asset.json";

const PRO_EVENT = "viny:open-pro";

export function openProDrawer() {
  window.dispatchEvent(new CustomEvent(PRO_EVENT));
}

const BENEFITS = [
  "Momentos ilimitados no Viny Ritual",
  "Scroll infinito no Explorar",
  "Remover ads",
  "Crie grupos privados",
  "Perfil em destaque na Viny",
  "Acesso antecipado a novas funcionalidades",
  "Templates exclusivos para seu mini site",
  "Selo de Embaixador",
  "Badge Pro no seu perfil",
  "Ajude a construir o futuro da Viny",
];

function ProContent({
  price,
  loading,
  error,
  onStart,
  onClose,
}: {
  price: string;
  loading: boolean;
  error: string | null;
  onStart: () => void;
  onClose?: () => void;
}) {
  return (
    <div className="relative grid h-full grid-cols-1 overflow-hidden rounded-[24px] bg-white lg:rounded-none lg:grid-cols-[0.9fr_1.1fr]">
      {/* Coluna preta com efeito (topo no mobile) */}
      <div className="relative h-[132px] overflow-hidden rounded-t-[24px] bg-[#0a0a0a] lg:h-auto lg:rounded-none">
        <ShaderBackground className="absolute inset-0" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/50 to-black/80" />
        <div className="relative z-10 flex h-full flex-col justify-between p-5 lg:p-8">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#9231FF]/25 text-white ring-1 ring-white/20 lg:h-14 lg:w-14">
            <Crown className="h-5 w-5 lg:h-7 lg:w-7" />
          </div>
          <div className="hidden lg:block">
            <span className="inline-flex items-center rounded-full bg-[#9231FF] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white">
              Pro
            </span>
            <h2 className="mt-4 font-display text-[30px] leading-[1.05] font-medium tracking-[-0.02em] text-white">
              Gostou da ideia?
            </h2>
            <p className="mt-2 text-sm text-white/70">
              Faça parte do começo da Viny! Seja Embaixador e apoie o crescimento da comunidade, garantindo o Pro por R$ 9,90/mês para sempre.
            </p>
          </div>
        </div>
      </div>

      {/* Coluna branca com os dados */}
      <div className="relative flex min-h-0 flex-col overflow-y-auto rounded-b-[24px] bg-white p-5 sm:p-7 lg:rounded-r-[24px] lg:rounded-bl-none lg:p-8">
        <div className="lg:hidden">
          <span className="inline-flex items-center rounded-full bg-[#9231FF] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white">
            Pro
          </span>
          <h2 className="mt-3 font-display text-[26px] leading-[1.1] font-medium tracking-[-0.02em] text-black">
            Gostou da ideia?
          </h2>
          <p className="mt-2 text-sm text-black/60">
            Faça parte do começo da Viny! Seja Embaixador e apoie o crescimento da comunidade, garantindo o Pro por R$ 9,90/mês para sempre.
          </p>
        </div>

        <div className="mt-5 flex items-end gap-2 lg:mt-0">
          <span className="text-[40px] font-semibold leading-none tracking-[-0.03em] text-black sm:text-[46px]">
            {price}
          </span>
          <span className="pb-1.5 text-sm text-black/50">/mês</span>
        </div>

        <ul className="mt-6 space-y-3">
          {BENEFITS.map((b) => (
            <li key={b} className="flex items-start gap-3 text-[14px] leading-relaxed">
              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#9231FF]/12 text-[#9231FF]">
                <Check className="h-3 w-3" />
              </span>
              <span className="text-black/80">{b}</span>
            </li>
          ))}
        </ul>

        {error && <p className="mt-4 text-[13px] font-medium text-red-600">{error}</p>}

        <button
          type="button"
          onClick={onStart}
          disabled={loading}
          className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#9231FF] px-6 text-[14px] font-bold text-white transition hover:brightness-110 disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Abrindo checkout…
            </>
          ) : (
            <>
              <Crown className="h-4 w-4 shrink-0" /> Assinar por {price}/mês
            </>
          )}
        </button>

        <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-black/45">
          Pagamento seguro via
          <img
            src={stripeLogoAsset.url}
            alt="Stripe"
            className="h-3.5 w-auto opacity-60"
          />
        </p>
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-30 grid h-9 w-9 place-items-center rounded-full bg-black/50 text-white/80 backdrop-blur transition hover:bg-black/70 hover:text-white"
          aria-label="Fechar"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}


export function ProDrawer() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { locale } = useLocale();
  const { isAuthenticated } = useCurrentUser();
  const isMobile = useIsMobile();
  const checkout = useServerFn(createCheckoutSession);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    const closeHandler = () => setOpen(false);
    window.addEventListener(PRO_EVENT, openHandler);
    window.addEventListener("viny:close-pro", closeHandler);
    return () => {
      window.removeEventListener(PRO_EVENT, openHandler);
      window.removeEventListener("viny:close-pro", closeHandler);
    };
  }, []);

  const price = locale === "en" ? "US$ 4" : "R$ 9,90";

  async function start() {
    if (!isAuthenticated) {
      window.location.href = "/auth";
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const origin = window.location.origin;
      const result = await checkout({
        data: {
          priceId: planPriceIdFor(locale),
          returnUrl: `${origin}/settings/billing?status=success&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${origin}/settings/billing?status=canceled`,
          environment: getStripeEnvironment(),
        },
      });
      if ("error" in result) throw new Error(result.error);
      if (!result.url) throw new Error("Stripe não retornou o link de pagamento");
      window.location.href = result.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível abrir o checkout");
      setLoading(false);
    }
  }

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          hideClose
          className="flex h-[92dvh] flex-col rounded-t-[24px] border-t border-black/10 bg-white p-0"
        >
          <ProContent
            price={price}
            loading={loading}
            error={error}
            onStart={start}
            onClose={() => setOpen(false)}
          />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl overflow-hidden border-0 bg-white p-0 shadow-2xl shadow-black/60 [&>button.absolute]:hidden">
        <div className="h-[560px]">
          <ProContent price={price} loading={loading} error={error} onStart={start} onClose={() => setOpen(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
